import {
  ConnectorAuthMode,
  ConnectorCategory,
  ConnectorInstanceStatus,
  HealthStatus,
  type ExternalItemType,
  type GetConnectorCatalogQuery,
  type GetEnabledConnectorsQuery,
} from "@axio/graphql/generated/graphql";

export type CatalogConnector = NonNullable<
  NonNullable<GetConnectorCatalogQuery["getConnectorCatalog"]["data"]>[number]
>;
export type EnabledConnectorInstance = NonNullable<
  NonNullable<GetEnabledConnectorsQuery["getEnabledConnectors"]["data"]>[number]
>;
export type ConnectorConnectionPattern = "setup-required" | "personal-only";
export type ConnectionStatus = "active" | "approved" | "working" | "degraded" | "failed" | "expired";
export type ConnectorActionResult = {
  success: boolean;
  message?: string | null;
  authorizationUrl?: string | null;
};
export type ConnectorSetupStrategy = NonNullable<CatalogConnector["setupConfig"]>["strategies"][number];
export type ConnectorCredentialField = ConnectorSetupStrategy["credentialFields"][number];
export type ConnectorCredentialConfig = Record<string, string>;
export type SavedConfigurationSummary = NonNullable<CatalogConnector["savedConfiguration"]>;

export type ConnectorApprovalSummary = {
  id: string;
  connectorId: string;
  approvedAt?: string;
  approvedBy?: string;
  userConnections?: number;
  status?: ConnectionStatus | null;
};

export type InstitutionalConnectionSummary = {
  id: string;
  connectorId: string;
  status: Exclude<ConnectionStatus, "approved">;
  authenticatedBy?: string;
  authenticatedAt?: string;
  lastSyncAt?: string;
  userCount?: number;
  scope?: string;
  errorMessage?: string;
};

export const CONNECTOR_CATEGORY_ORDER: readonly ConnectorCategory[] = [
  ConnectorCategory.CloudStorage,
  ConnectorCategory.VideoService,
  ConnectorCategory.LmsRepository,
  ConnectorCategory.AdminKnowledgeBase,
  ConnectorCategory.LibraryRepository,
  ConnectorCategory.WebCms,
];

export const CONNECTOR_CATEGORY_LABELS: Record<ConnectorCategory, string> = {
  [ConnectorCategory.CloudStorage]: "Cloud Storage",
  [ConnectorCategory.VideoService]: "Video",
  [ConnectorCategory.LmsRepository]: "LMS",
  [ConnectorCategory.AdminKnowledgeBase]: "Knowledge Base",
  [ConnectorCategory.LibraryRepository]: "Library Repository",
  [ConnectorCategory.WebCms]: "Web CMS",
};

export type ConnectorAuthPresentation = {
  variant: "setup" | "personal";
  label: "Setup Required" | "Personal";
  ctaLabel: "Configure" | "Manage";
};

export const getConnectorSetupStrategies = (connector: CatalogConnector): ConnectorSetupStrategy[] =>
  connector.setupConfig?.strategies ?? [];

export const hasConnectorSetupFlow = (connector: CatalogConnector): boolean =>
  getConnectorSetupStrategies(connector).length > 0;

export const getSetupStrategy = (
  connector: CatalogConnector,
  authMode: ConnectorAuthMode
): ConnectorSetupStrategy | null =>
  getConnectorSetupStrategies(connector).find((strategy) => strategy.authMode === authMode) ?? null;

export const hasPersonalUserOauth = (connector: CatalogConnector): boolean =>
  connector.authModes.includes(ConnectorAuthMode.UserOauth2);

export const usesConnectorApprovalFlow = (connector: CatalogConnector): boolean =>
  hasPersonalUserOauth(connector) && !hasConnectorSetupFlow(connector);

export const hasSavedConfiguration = (connector: CatalogConnector): boolean => connector.savedConfiguration != null;

export const getDefaultSetupAuthMode = (connector: CatalogConnector): ConnectorAuthMode | null => {
  const setupStrategies = getConnectorSetupStrategies(connector);

  if (setupStrategies.length > 0) {
    return setupStrategies[0]?.authMode ?? null;
  }

  if (hasPersonalUserOauth(connector)) {
    return ConnectorAuthMode.UserOauth2;
  }

  return null;
};

export const getDefaultSetupStrategy = (connector: CatalogConnector): ConnectorSetupStrategy | null => {
  const authMode = getDefaultSetupAuthMode(connector);

  return authMode ? getSetupStrategy(connector, authMode) : null;
};

export const getSavedConfigurationSummary = (connector: CatalogConnector): SavedConfigurationSummary | null =>
  connector.savedConfiguration ?? null;

export const usesSavedConfigurationRedirect = (connector: CatalogConnector): boolean =>
  connector.savedConfiguration?.authMode === ConnectorAuthMode.UserOauth2;

export const getSavedConfigurationActionLabel = (connector: CatalogConnector): "Test OAuth" | "Test Connection" =>
  usesSavedConfigurationRedirect(connector) ? "Test OAuth" : "Test Connection";

export const getConnectorAuthPresentation = (connector: CatalogConnector): ConnectorAuthPresentation => {
  if (hasConnectorSetupFlow(connector)) {
    return {
      variant: "setup",
      label: "Setup Required",
      ctaLabel: "Configure",
    };
  }

  if (hasPersonalUserOauth(connector)) {
    return {
      variant: "personal",
      label: "Personal",
      ctaLabel: "Manage",
    };
  }

  return {
    variant: "setup",
    label: "Setup Required",
    ctaLabel: "Configure",
  };
};

export const getConnectorConnectionPattern = (connector: CatalogConnector): ConnectorConnectionPattern => {
  const authPresentation = getConnectorAuthPresentation(connector);

  switch (authPresentation.variant) {
    case "setup":
      return "setup-required";
    case "personal":
      return "personal-only";
  }
};

export const formatEnumLabel = (value: string): string =>
  value
    .toLowerCase()
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

export const formatConnectorAuthModeLabel = (authMode: ConnectorAuthMode): string => {
  switch (authMode) {
    case ConnectorAuthMode.UserOauth2:
      return "User OAuth 2.0";
    case ConnectorAuthMode.InstitutionalOauth2:
      return "Institutional OAuth 2.0";
    case ConnectorAuthMode.ApiKey:
      return "API Key";
    case ConnectorAuthMode.SessionToken:
      return "Session Token";
    case ConnectorAuthMode.ServiceAccount:
      return "Service Account";
  }
};

export const formatSupportedContentTypeLabel = (contentType: ExternalItemType): string => formatEnumLabel(contentType);

export const groupConnectorsByCategory = (catalog: CatalogConnector[]) =>
  CONNECTOR_CATEGORY_ORDER.map((category) => ({
    category,
    connectors: catalog.filter((connector) => connector.category === category),
  })).filter((group) => group.connectors.length > 0);

const formatDateLabel = (value: string): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));

const formatOptionalDateLabel = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const getConfigSummaryRecord = (configSummary: unknown): Record<string, unknown> | null => {
  if (!configSummary) {
    return null;
  }

  if (typeof configSummary === "string") {
    try {
      const parsed = JSON.parse(configSummary);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }

  if (typeof configSummary !== "object" || Array.isArray(configSummary)) {
    return null;
  }

  return configSummary as Record<string, unknown>;
};

const readConfigSummaryString = (configSummary: unknown, key: string): string | undefined => {
  const record = getConfigSummaryRecord(configSummary);

  if (!record) {
    return undefined;
  }

  const value = record[key];

  if (typeof value !== "string" || value.trim() === "") {
    return undefined;
  }

  return value;
};

const getInstitutionalScopeLabel = (connector: CatalogConnector, configSummary: unknown): string | undefined => {
  const configuredScope = readConfigSummaryString(configSummary, "scope");

  if (configuredScope) {
    return configuredScope;
  }

  if (connector.capabilities.length === 0) {
    return undefined;
  }

  return connector.capabilities.map(formatEnumLabel).join(", ");
};

const getInstitutionalLastSyncLabel = (instance: EnabledConnectorInstance): string | undefined =>
  formatOptionalDateLabel(instance.healthStatus?.lastCheckedAt) ??
  formatOptionalDateLabel(readConfigSummaryString(instance.configSummary, "lastSyncAt")) ??
  formatOptionalDateLabel(readConfigSummaryString(instance.configSummary, "lastValidatedAt")) ??
  formatOptionalDateLabel(instance.lastModified) ??
  formatOptionalDateLabel(instance.created);

const getInstitutionalConnectionStatus = (
  instance: EnabledConnectorInstance
): InstitutionalConnectionSummary["status"] | null => {
  if (instance.healthStatus?.status === HealthStatus.Failed || instance.status === ConnectorInstanceStatus.Error) {
    return "failed";
  }

  if (instance.healthStatus?.status === HealthStatus.Degraded) {
    return "degraded";
  }

  if (instance.status === ConnectorInstanceStatus.Active) {
    return "active";
  }

  return null;
};

export const buildConnectorStateMaps = (
  catalog: CatalogConnector[],
  enabledConnectors: EnabledConnectorInstance[]
): {
  approvalMap: Map<string, ConnectorApprovalSummary>;
  connectionMap: Map<string, InstitutionalConnectionSummary>;
} => {
  const connectorsByType = new Map(catalog.map((connector) => [connector.connectorType, connector]));
  const approvalMap = new Map<string, ConnectorApprovalSummary>();
  const connectionMap = new Map<string, InstitutionalConnectionSummary>();

  for (const instance of enabledConnectors) {
    const connector = connectorsByType.get(instance.connectorType);

    if (!connector) {
      continue;
    }

    if (hasPersonalUserOauth(connector) && instance.status !== ConnectorInstanceStatus.Inactive) {
      approvalMap.set(connector.id, {
        id: instance.id,
        connectorId: connector.id,
        approvedAt: formatDateLabel(instance.created),
        approvedBy:
          readConfigSummaryString(instance.configSummary, "approvedBy") ??
          readConfigSummaryString(instance.configSummary, "configuredBy") ??
          readConfigSummaryString(instance.configSummary, "authenticatedBy"),
        userConnections: instance.healthStatus?.userConnectionCount ?? undefined,
        status: instance.status === ConnectorInstanceStatus.Active ? "approved" : "failed",
      });
      continue;
    }

    if (!hasConnectorSetupFlow(connector) || hasPersonalUserOauth(connector)) {
      continue;
    }

    const status = getInstitutionalConnectionStatus(instance);

    if (!status) {
      continue;
    }

    connectionMap.set(connector.id, {
      id: instance.id,
      connectorId: connector.id,
      status,
      authenticatedBy: readConfigSummaryString(instance.configSummary, "authenticatedBy"),
      authenticatedAt: formatDateLabel(instance.created),
      lastSyncAt: getInstitutionalLastSyncLabel(instance),
      userCount: instance.healthStatus?.userConnectionCount ?? undefined,
      scope: getInstitutionalScopeLabel(connector, instance.configSummary),
      errorMessage: instance.healthStatus?.errorMessage ?? undefined,
    });
  }

  return {
    approvalMap,
    connectionMap,
  };
};
